import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import API from '../context/API';

export interface Option {
  id: number | string;
  option: string;
  price?: number;
  description?: string;
  sold_out?: boolean;
  sequence_number?: number;
  customization?: number | string;
}

export interface CreateOptionData {
  option: string;
  price: number;
  description?: string;
  customization: number | string;
}

export interface UpdateOptionData {
  option?: string;
  price?: number;
  description?: string;
  sold_out?: boolean;
  sequence_number?: number | string;
  customization?: number | string;
}

export interface OptionsState {
  options: Option[];
  filteredOptions: Option[];
  loading: boolean;
  error: string | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  updatingSoldOut: number | string | null;
}

const initialState: OptionsState = {
  options: [],
  filteredOptions: [],
  loading: false,
  error: null,
  creating: false,
  updating: false,
  deleting: false,
  updatingSoldOut: null,
};

// Async Thunks
export const fetchOptions = createAsyncThunk(
  'options/fetchOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get('/options');
      if (response.data.success) {
        const optionsData = response.data.data.sort((a: Option, b: Option) => {
          const nameA = (a.option || '').toLowerCase();
          const nameB = (b.option || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        return optionsData;
      } else {
        return rejectWithValue('Failed to fetch options.');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch options.');
    }
  }
);

export const createOption = createAsyncThunk(
  'options/createOption',
  async (data: CreateOptionData, { rejectWithValue }) => {
    try {
      const response = await API.post('/options', {
        option: data.option,
        price: parseFloat(String(data.price)),
        description: data.description || '',
        customization: data.customization.toString(),
      });

      if (response && response.data) {
        const isSuccess =
          response.data.success || response.status === 200 || response.status === 201;
        const newOption = response.data.data || response.data;

        if (isSuccess && newOption) {
          return newOption;
        } else {
          return rejectWithValue(response.data?.message || 'Failed to create the option.');
        }
      } else {
        return rejectWithValue('No response from server. Please try again.');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create option.');
    }
  }
);

export const updateOption = createAsyncThunk(
  'options/updateOption',
  async ({ id, data }: { id: number | string; data: UpdateOptionData }, { rejectWithValue }) => {
    try {
      const cleanPayload: any = {};
      if (data.option !== undefined) cleanPayload.option = data.option;
      if (data.price !== undefined) cleanPayload.price = data.price;
      if (data.description !== undefined) cleanPayload.description = data.description;
      if (data.sold_out !== undefined) cleanPayload.sold_out = data.sold_out;
      if (data.sequence_number !== undefined) cleanPayload.sequence_number = String(data.sequence_number);
      if (data.customization !== undefined) cleanPayload.customization = data.customization;

      const response = await API.put(`/options/${id}`, cleanPayload);

      if (response.data.success) {
        return response.data.data;
      } else {
        return rejectWithValue('Failed to update option.');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update option.');
    }
  }
);

export const deleteOption = createAsyncThunk(
  'options/deleteOption',
  async (id: number | string, { rejectWithValue }) => {
    try {
      await API.delete(`/options/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete option.');
    }
  }
);

// Slice
const optionsSlice = createSlice({
  name: 'options',
  initialState,
  reducers: {
    setFilteredOptions: (state, action: PayloadAction<Option[]>) => {
      state.filteredOptions = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetOptions: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch options
      .addCase(fetchOptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOptions.fulfilled, (state, action) => {
        state.loading = false;
        state.options = action.payload;
        state.filteredOptions = action.payload;
      })
      .addCase(fetchOptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create option
      .addCase(createOption.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createOption.fulfilled, (state, action) => {
        state.creating = false;
        state.options = [...state.options, action.payload];
        state.filteredOptions = [...state.filteredOptions, action.payload];
      })
      .addCase(createOption.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      })
      // Update option
      .addCase(updateOption.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateOption.fulfilled, (state, action) => {
        state.updating = false;
        const index = state.options.findIndex((opt) => opt.id === action.payload.id);
        if (index !== -1) {
          state.options[index] = action.payload;
        }
        const filteredIndex = state.filteredOptions.findIndex((opt) => opt.id === action.payload.id);
        if (filteredIndex !== -1) {
          state.filteredOptions[filteredIndex] = action.payload;
        }
      })
      .addCase(updateOption.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload as string;
      })
      // Delete option
      .addCase(deleteOption.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteOption.fulfilled, (state, action) => {
        state.deleting = false;
        state.options = state.options.filter((opt) => opt.id !== action.payload);
        state.filteredOptions = state.filteredOptions.filter((opt) => opt.id !== action.payload);
      })
      .addCase(deleteOption.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFilteredOptions, clearError, resetOptions } = optionsSlice.actions;

export default optionsSlice.reducer;

